/**
 * @file 解説に添える関数のグラフ (SVG)。
 *
 * @what: 曲線 (x → y の関数) を区間で標本化して折れ線で描き、曲線の下の面積・多角形の
 *   塗り・円・線分・名前つきの点・目盛りの名前を重ねる。外部の描画ライブラリは使わず、色は
 *   study.css の変数を引く。円は x と y の縮尺が違うので楕円として表示する。
 * @why: 解説の「背景にある図形的な意味」は式だけでは伝わらず、台形と曲線の間の面積のように
 *   図で見せる必要がある (代表判断、2026-09-05)。内容 (content) 側は関数と区間だけを書けばよく、
 *   軸の変換や標本化はここが受け持つ。
 * @failure: 描画のみ。値域の外に出た点は線を切って描かない (無限大や発散で SVG が壊れない
 *   ようにする)。
 */

export interface PlotCurve {
  f: (x: number) => number;
  /** 凡例の文字 (曲線の右端に添える)。 */
  label?: string;
  dashed?: boolean;
}

export interface PlotSpec {
  xRange: [number, number];
  yRange: [number, number];
  curves: readonly PlotCurve[];
  /** 曲線と x 軸の間を塗る区間である。 */
  areaUnder?: { f: (x: number) => number; from: number; to: number; label?: string };
  /** 任意の多角形を塗る (最後の点から最初の点へ自動で閉じる)。 */
  polygon?: { points: readonly (readonly [number, number])[]; label?: string };
  /** 円 (中心と半径)。 */
  circles?: readonly { cx: number; cy: number; r: number; label?: string; dashed?: boolean }[];
  /** 線分である。 */
  segments?: readonly {
    from: readonly [number, number];
    to: readonly [number, number];
    label?: string;
    dashed?: boolean;
  }[];
  /** 名前つきの点である。 */
  points?: readonly { x: number; y: number; label?: string }[];
  xTicks?: readonly { x: number; label: string }[];
  yTicks?: readonly { y: number; label: string }[];
  /** 図の下に出す短い説明である。 */
  caption: string;
}

const W = 520;
const H = 320;
const PAD = { top: 20, right: 90, bottom: 36, left: 44 };
const SAMPLES = 240;

export function FunctionPlot({ spec }: { spec: PlotSpec }) {
  const [x0, x1] = spec.xRange;
  const [y0, y1] = spec.yRange;
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const sx = (x: number) => PAD.left + ((x - x0) / (x1 - x0)) * innerW;
  const sy = (y: number) => PAD.top + ((y1 - y) / (y1 - y0)) * innerH;
  const inRange = (y: number) => Number.isFinite(y) && y >= y0 && y <= y1;

  const curvePath = (f: (x: number) => number): string => {
    const parts: string[] = [];
    let pen = false;
    for (let i = 0; i <= SAMPLES; i++) {
      const x = x0 + ((x1 - x0) * i) / SAMPLES;
      const y = f(x);
      if (!inRange(y)) {
        pen = false;
        continue;
      }
      parts.push(`${pen ? "L" : "M"}${sx(x).toFixed(1)} ${sy(y).toFixed(1)}`);
      pen = true;
    }
    return parts.join(" ");
  };

  const areaPath = (): string | null => {
    const a = spec.areaUnder;
    if (!a) return null;
    const pts: string[] = [];
    for (let i = 0; i <= 60; i++) {
      const x = a.from + ((a.to - a.from) * i) / 60;
      pts.push(`${sx(x).toFixed(1)} ${sy(Math.min(Math.max(a.f(x), y0), y1)).toFixed(1)}`);
    }
    const base = sy(Math.min(Math.max(0, y0), y1));
    return `M${sx(a.from).toFixed(1)} ${base} L${pts.join(" L")} L${sx(a.to).toFixed(1)} ${base} Z`;
  };

  const polygonPoints = spec.polygon
    ? spec.polygon.points.map(([x, y]) => `${sx(x).toFixed(1)},${sy(y).toFixed(1)}`).join(" ")
    : null;

  const axisY = sy(Math.min(Math.max(0, y0), y1));
  const axisX = sx(Math.min(Math.max(0, x0), x1));
  const area = areaPath();

  return (
    <figure className="study-plot">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={spec.caption}
        className="study-plot-svg"
      >
        {area ? <path d={area} className="study-plot-area" /> : null}
        {polygonPoints ? <polygon points={polygonPoints} className="study-plot-polygon" /> : null}
        <line x1={PAD.left} x2={W - PAD.right} y1={axisY} y2={axisY} className="study-plot-axis" />
        <line x1={axisX} x2={axisX} y1={PAD.top} y2={H - PAD.bottom} className="study-plot-axis" />
        {(spec.xTicks ?? []).map((t) => (
          <g key={`x${t.x}`}>
            <line
              x1={sx(t.x)}
              x2={sx(t.x)}
              y1={PAD.top}
              y2={H - PAD.bottom}
              className="study-plot-guide"
            />
            <text
              x={sx(t.x)}
              y={H - PAD.bottom + 16}
              textAnchor="middle"
              className="study-plot-tick"
            >
              {t.label}
            </text>
          </g>
        ))}
        {(spec.yTicks ?? []).map((t) => (
          <text
            key={`y${t.y}`}
            x={PAD.left - 6}
            y={sy(t.y) + 4}
            textAnchor="end"
            className="study-plot-tick"
          >
            {t.label}
          </text>
        ))}
        {(spec.circles ?? []).map((c, i) => (
          <g key={`c${i}`}>
            <ellipse
              cx={sx(c.cx)}
              cy={sy(c.cy)}
              rx={(c.r / (x1 - x0)) * innerW}
              ry={(c.r / (y1 - y0)) * innerH}
              className={
                c.dashed ? "study-plot-circle study-plot-curve-dashed" : "study-plot-circle"
              }
            />
            {c.label ? (
              <text
                x={sx(c.cx)}
                y={sy(c.cy) - (c.r / (y1 - y0)) * innerH - 6}
                textAnchor="middle"
                className="study-plot-label"
              >
                {c.label}
              </text>
            ) : null}
          </g>
        ))}
        {(spec.segments ?? []).map((s, i) => (
          <g key={`s${i}`}>
            <line
              x1={sx(s.from[0])}
              y1={sy(s.from[1])}
              x2={sx(s.to[0])}
              y2={sy(s.to[1])}
              className={
                s.dashed ? "study-plot-segment study-plot-curve-dashed" : "study-plot-segment"
              }
            />
            {s.label ? (
              <text
                x={sx((s.from[0] + s.to[0]) / 2) + 6}
                y={sy((s.from[1] + s.to[1]) / 2) - 6}
                className="study-plot-label"
              >
                {s.label}
              </text>
            ) : null}
          </g>
        ))}
        {spec.curves.map((c, i) => (
          <path
            key={i}
            d={curvePath(c.f)}
            className={c.dashed ? "study-plot-curve study-plot-curve-dashed" : "study-plot-curve"}
          />
        ))}
        {spec.curves.map((c, i) =>
          c.label ? (
            <text
              key={`l${i}`}
              x={W - PAD.right + 6}
              y={sy(Math.min(Math.max(c.f(x1), y0), y1)) + 4 + i * 14}
              className="study-plot-label"
            >
              {c.label}
            </text>
          ) : null,
        )}
        {(spec.points ?? []).map((p, i) => (
          <g key={`p${i}`}>
            <circle cx={sx(p.x)} cy={sy(p.y)} r={3.5} className="study-plot-point" />
            {p.label ? (
              <text x={sx(p.x) + 7} y={sy(p.y) - 7} className="study-plot-label">
                {p.label}
              </text>
            ) : null}
          </g>
        ))}
        {spec.areaUnder?.label ? (
          <text
            x={sx((spec.areaUnder.from + spec.areaUnder.to) / 2)}
            y={sy(
              Math.min(spec.areaUnder.f(spec.areaUnder.from), spec.areaUnder.f(spec.areaUnder.to)) /
                2,
            )}
            textAnchor="middle"
            className="study-plot-label"
          >
            {spec.areaUnder.label}
          </text>
        ) : null}
        {spec.polygon?.label ? (
          <text
            x={sx(spec.polygon.points.reduce((s, p) => s + p[0], 0) / spec.polygon.points.length)}
            y={
              sy(spec.polygon.points.reduce((s, p) => s + p[1], 0) / spec.polygon.points.length) - 6
            }
            textAnchor="middle"
            className="study-plot-label"
          >
            {spec.polygon.label}
          </text>
        ) : null}
      </svg>
      <figcaption className="study-plot-caption">{spec.caption}</figcaption>
    </figure>
  );
}
